import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BlockIcon from "@mui/icons-material/Block";
import CloseIcon from "@mui/icons-material/Close";
import CommentIcon from "@mui/icons-material/Comment";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import GradingIcon from "@mui/icons-material/Grading";
import GroupsIcon from "@mui/icons-material/Groups";
import IosShareIcon from "@mui/icons-material/IosShare";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import UndoIcon from "@mui/icons-material/Undo";
import WorkIcon from "@mui/icons-material/Work";
import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { skipToken } from "@reduxjs/toolkit/query";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import { uniqBy, chain } from "lodash";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { Masked, MaskedTeam } from "work/_shared/masked-fields";
import {
  ActionsContainer,
  ButtonContainer,
  Content,
  ProposalTab,
  Header,
  TabContent,
  TabsContainer,
} from "work/_views/work-page-styled-components";
import {
  useApproveProposalMutation,
  useCancelProposalMutation,
  useGetProposalByIdQuery,
  useGiveReviewerApprovalMutation,
  useHireProposalMutation,
  useQuitProposalTeamMutation,
  useRejectProposalMutation,
  useRequestProposalRevisionMutation,
  useSaveProposalMutation,
  useShareProposalMutation,
} from "work/proposals/_api/proposal-api";
import {
  addProposal,
  getProposalById,
  isProposalLoadIssue,
  resetProposalSessionByProposalId,
  updateProposal
} from "work/proposals/_store/proposals-redux-slice";
import {
  CommentButtonLabel,
  CommentButton,
  Fieldset,
  HighlightCommentButton,
  ProposalActionButton,
  ProposalActions,
  ReviewerBadge,
} from "work/proposals/_views/_shared/proposal-styled-components";
import ConflictsTab from "work/proposals/_views/active/tabs/active-proposal-conflicts-tab";
import DetailsTab from "work/proposals/_views/active/tabs/active-proposal-details-tab";
import DiscountTab from "work/proposals/_views/active/tabs/active-proposal-discount-tab";
import FeeScheduleTab from "work/proposals/_views/active/tabs/active-proposal-fee-schedule-tab";
import PoliciesTab from "work/proposals/_views/active/tabs/active-proposal-policies-tab";
import TeamTab from "work/proposals/_views/active/tabs/active-proposal-team-tab";
import {
  clearUnsavedCommentsByProposalId,
  getArePendingComments,
  getAreUnsavedComments,
  publishComments,
  saveComments,
  updateAutoGeneratedComments,
  updateThreadSubscribers,
} from "work/proposals/comments/_store/comments-redux-slice";
import CommentsDrawer from "work/proposals/comments/_view/comments-drawer";
import AutoCommentGenerator from "work/proposals/comments/auto-comments/auto-comment-generator";
import {
  getCommentThreadsByProposal,
  getErrorLoadingCommentThreadsByProposal,
  getIsLoadingCommentThreadsByProposal,
  populateCommentThreadsByProposal,
} from "work/proposals/comments/threads/_store/comment-thread-redux-slice";
import IssueTraverser from "work/proposals/issues/_views/proposal-issue-traverser";
import ProposalIssuesBadge from "work/proposals/issues/_views/proposal-issues-badge";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import Proposal from "work/proposals/proposal";
import ProposalField, {
  ProposalFieldCategory,
  ProposalFieldCategoryType,
  ProposalFieldName,
} from "work/proposals/proposal-fields/proposal-field";
import ProposalRedline from "work/proposals/redlining/proposal-redline";
import ReviewerSelector from "work/proposals/reviewers/_views/reviewer-selector";
import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import LoadingButton from "_shared/_views/loading/loading-button";
import { PageTab } from "work/proposals/_views/active/active-proposal-page-tabs";
import { ProposalJSON } from "work/proposals/_schemas/proposal-schema";

const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));
const PageContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  height: '100%'
}));
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "space-between",
  paddingRight: theme.spacing(3)
}));
const TitleContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row"
}));
const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(2)
}));
const Main = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'showCommentDrawer'
})<{
  open?: boolean;
  showCommentDrawer?: boolean;
}>(({ theme, showCommentDrawer }) => ({
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2)
  },
  display: 'flex',
  height: '100%',
  flexDirection: 'column',
  flex: 1,
  padding: theme.spacing(3),
  paddingRight: 0,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginRight: showCommentDrawer ? '-400px' : 0,
  /**
   * This is necessary to enable the selection of content. In the DOM, the stacking order is determined
   * by the order of appearance. Following this rule, elements appearing later in the markup will overlay
   * those that appear earlier. Since the Drawer comes after the Main content, this adjustment ensures
   * proper interaction with the underlying content.
   */
  position: 'relative',
  width: '100%',
  variants: [
    {
      props: ({ open }) => open,
      style: {
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginRight: 0,
      },
    },
  ],
}));

type ActiveProposalAction = "revise" | "review" | "view";
const ActiveProposalActions = new Set<ActiveProposalAction>(["revise", "review", "view"]);

export default function ActiveProposalPage() {
  // #region Refs
  const navigate = useNavigate();
  const {
    action: actionParam,
    id: idParam,
    pageTab: pageTabParam
  } = useParams();
  const dispatch = useAttorneyHubDispatch();
  const confirm = useConfirmDialog();
  const session = useSession();

  const pageAction: ActiveProposalAction = actionParam as ActiveProposalAction;

  // Unsupported action
  if (!ActiveProposalActions.has(pageAction)) {
    throw new Response("Not Found", { status: 404 });
  }
  // Proposal id is required
  if (!idParam) {
    throw new Response("Not Found", { status: 404 });
  }

  // #region State
  const [isSaving, setIsSaving] = React.useState(false);

  const [userSelectedReviewField, setUserSelectedReviewField] = React.useState<ProposalField | undefined>(undefined);

  const [currentCommentField, setCurrentCommentField] = React.useState<ProposalField | undefined>();
  const [commentsPanelIsOpen, setCommentsPanelIsOpen] = React.useState<boolean>(false);
  const [commentsPanelTitle, setCommentsPanelTitle] = React.useState<string | undefined>();

  const [reviewerSelectorAnchor, setReviewerSelectorAnchor] = React.useState<HTMLButtonElement>();
  const [isManagingReviewers, setIsManagingReviewers] = React.useState<boolean>(false);

  // #region Globals
  const proposalId = useMemo(() => (idParam ? new Guid(idParam) : undefined), [idParam]);
  const disableEditing = pageAction === "view" || !pageAction;
  const activeTab = pageTabParam as PageTab;

  // #region Proposal Queries
  const [requestRevisionAPICommand, requestRevisionResult] = useRequestProposalRevisionMutation();
  const [approveProposalAPICommand, approveProposalResult] = useApproveProposalMutation();
  const [rejectProposalAPICommand, rejectProposalResult] = useRejectProposalMutation();
  const [hireProposalAPICommand, hireProposalResult] = useHireProposalMutation();
  const [cancelProposalAPICommand, cancelProposalResult] = useCancelProposalMutation();
  const [quitProposalTeamAPICommand, quitProposalTeamResult] = useQuitProposalTeamMutation();
  const [giveReviewerApprovalAPICommand, giveReviewerApprovalResult] = useGiveReviewerApprovalMutation();
  const [saveProposalAPICommand, saveProposalResult] = useSaveProposalMutation();
  const [shareProposalAPICommand, shareProposalResult] = useShareProposalMutation();

  const proposalByIdAPIQuery = useGetProposalByIdQuery(
    proposalId && !proposalId.isEmpty ? { proposalId: proposalId.value, session } : skipToken,
  );
  const proposalJson = useSelector(getProposalById(proposalId));
  const proposal = useMemo(() => {
    if (!proposalJson || isProposalLoadIssue(proposalJson) || !session.user) return null;
    return Proposal.fromJSON({
      json: proposalJson,
      currentUser: session.user,
      options: { withRedline: true }
    });
  }, [proposalJson, session.user]);

  // #region Comments Queries
  const commentThreads = useSelector(getCommentThreadsByProposal(proposal));
  const isLoadingCommentThreads = useSelector(getIsLoadingCommentThreadsByProposal(proposal));
  const wasErrorLoadingCommentThreads = useSelector(getErrorLoadingCommentThreadsByProposal(proposal));
  const isLoading = proposalByIdAPIQuery.isLoading || isLoadingCommentThreads;
  const comments = useSelector((state: RootState) => state.comments.entries);
  const arePendingComments = useSelector(getArePendingComments(proposal));
  const areUnsavedComments = useSelector(getAreUnsavedComments(proposal));

  // #region Permissions Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  const proposalRef = useRef(proposal);
  const arePendingCommentsRef = useRef(arePendingComments);
  const areUnsavedCommentsRef = useRef(areUnsavedComments);

  const isSubmitting =
    requestRevisionResult.isLoading ||
    approveProposalResult.isLoading ||
    rejectProposalResult.isLoading ||
    hireProposalResult.isLoading ||
    cancelProposalResult.isLoading ||
    quitProposalTeamResult.isLoading ||
    giveReviewerApprovalResult.isLoading;

  const issues = proposal?.redline ? ProposalIssues.fromRedline(proposal?.redline) : undefined;

  const fieldsPendingReviewForActiveTab = useMemo(() => {
    const proposalFieldCategories = Object.values(ProposalFieldCategory);
    const proposalFieldNames = Object.values(ProposalFieldName);
    let activeCategory: ProposalFieldCategoryType | undefined;
    switch (activeTab) {
      case PageTab.Details:
        activeCategory = ProposalFieldCategory.Details;
        break;
      case PageTab.Team:
        activeCategory = ProposalFieldCategory.Team;
        break;
      case PageTab.FeeSchedule:
        activeCategory = ProposalFieldCategory.FeeSchedule;
        break;
      case PageTab.Conflicts:
        activeCategory = ProposalFieldCategory.Conflicts;
        break;
      case PageTab.Policies:
        activeCategory = ProposalFieldCategory.Policies;
        break;
      case PageTab.Discount:
        activeCategory = ProposalFieldCategory.Discount;
        break;
    }
    const tabIssues: ProposalField[] = uniqBy(
      issues?.entries.map((issue) => issue.field).filter((field) => field.category === activeCategory) ?? [],
      (field) => field.toJSON(),
    );

    const tabUnreadComments: ProposalField[] = uniqBy(
      commentThreads
        ?.filter((thread) => thread.numUnreadMessages && thread.field.category === proposalFieldCategoryFromPageTab(activeTab))
        .map((thread) => thread.field) ?? [],
      (field) => field.toJSON(),
    );

    return chain([...tabUnreadComments, ...tabIssues])
      .uniqBy((field) => `${field.category}-${field.name}-${field.id?.value ?? ""}`)
      .sortBy([
        (field) => proposalFieldCategories.indexOf(field.category),
        (field) => proposalFieldNames.indexOf(field.name),
      ])
      .value();
  }, [activeTab, commentThreads, issues?.entries]);

  const activeReviewField = useMemo(() => {
    if (userSelectedReviewField?.category === proposalFieldCategoryFromPageTab(activeTab)) {
      return userSelectedReviewField;
    }
    return fieldsPendingReviewForActiveTab[0];
  }, [activeTab, userSelectedReviewField, fieldsPendingReviewForActiveTab]);

  const isReviewing = useMemo(() => {
    return (
      [...(proposal?.clientReviewers ?? []), ...(proposal?.vendorReviewers ?? [])].some((reviewer) =>
        reviewer.userId.isEqualTo(session.user?.id),
      ) ?? false
    );
  }, [proposal?.clientReviewers, proposal?.vendorReviewers, session.user?.id]);
  const vendorReviewerCount = proposal?.vendorReviewers?.length ?? 0;
  const clientReviewerCount = proposal?.clientReviewers?.length ?? 0;
  const hasReviewers = session.context?.viewingAsVendor ? vendorReviewerCount > 0 : clientReviewerCount > 0;

  const generalFieldUnreadCount =
    commentThreads
      ?.filter((thread) => thread.field.isEqualTo(ProposalField.General))
      .reduce((acc, thread) => acc + (thread.numUnreadMessages ?? 0), 0) ?? 0;

  const generateChangeAutoComments = React.useCallback(
    (updatedProposal: Proposal) => {
      if (!updatedProposal?.redline) return;
      const autoComments = new AutoCommentGenerator(
        session,
        updatedProposal,
        undefined,
        commentThreads ?? [],
      ).generateAutoCommentsFromRedline();
      dispatch(updateAutoGeneratedComments(autoComments));
    },
    [session, commentThreads, dispatch],
  );

  const handleTraverseToNewField = (field?: ProposalField) => {
    if (!field) return;
    const newTab = pageTabFromProposalFieldCategory(field.category);
    if (!newTab) return;
    navigate(`/proposals/active/${pageAction}/${proposalId?.value}/${newTab}`);
    setUserSelectedReviewField(field);
  };

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if (!session?.canSwitchContext || !proposal) return;
    if (
      session.context?.viewingAsVendor &&
      MaskedTeam.isPresentNotMasked(proposal.clientLeader) &&
      proposal.clientLeader?.userId.isEqualTo(session.user?.id)
    ) {
      (async () => {
        try {
          await session.setAccountViewType(AccountType.Client);
        } catch (error) {
          console.error("Error setting account view type:", error);
          enqueueSnackbar("Failed to switch to client account", { variant: "error" });
        }
      })();
    } else if (
      Masked.isNotMasked(proposal.vendorTeam) &&
      (proposal.vendorTeam?.leader?.userId.isEqualTo(session.user?.id) ||
        proposal.vendorTeam?.members?.some((member) => member.userId.isEqualTo(session.user?.id)))
    ) {
      (async () => {
        try {
          await session.setAccountViewType(AccountType.Vendor);
        } catch (error) {
          console.error("Error setting account view type:", error);
          enqueueSnackbar("Failed to switch to vendor account", { variant: "error" });
        }
      })();
    }
  }, [proposal, session]);

  useEffect(() => {
    if (
      !proposal ||
      proposal.supersededById ||
      isLoading ||
      wasErrorLoadingCommentThreads ||
      commentThreads
    )
      return;
    dispatch(populateCommentThreadsByProposal({ proposal, session }));
  }, [proposal, isLoading, wasErrorLoadingCommentThreads, commentThreads, dispatch, session]);

  useEffect(() => {
    proposalRef.current = proposal;
    arePendingCommentsRef.current = arePendingComments;
    areUnsavedCommentsRef.current = areUnsavedComments;
  }, [proposal, areUnsavedComments, arePendingComments, comments]);

  useEffect(() => {
    if (!proposal || proposal.supersededById) return;
    generateChangeAutoComments(proposal);
  }, [generateChangeAutoComments, proposal]);

  useEffect(() => {
    if (proposalByIdAPIQuery.isLoading) return;

    if (proposal?.supersededById) {
      navigate(`/proposals/active/revise/${proposal.supersededById.value}/details`, {
        replace: true
      });
      return;
    }
    if (proposal?.isHired) {
      navigate(`/work/active/view/${proposal.workAgreementId?.value}`, {
        replace: true
      });
    }

    if (!session.user || !pageAction) {
      console.error("Unable to open proposal. Missing required parameters.");
      enqueueSnackbar("Unable to open proposal", { variant: "error" });
      navigate("/proposals/active", { replace: true });
      return;
    }

    if (!proposal && proposalId && !proposalId.isEmpty) {
      if (pageAction === "review" && idParam) {
        enqueueSnackbar("You are no longer a reviewer on this proposal", {
          variant: "warning",
        });
      } else if (idParam) {
        enqueueSnackbar("Proposal not found", { variant: "warning" });
      }

      navigate('/proposals/active', { replace: true });
    }
  }, [
    proposal,
    proposalByIdAPIQuery.isLoading,
    proposalId,
    idParam,
    pageAction,
    activeTab,
    session.user,
    navigate,
  ]);

  function handleTabChange(_event: React.ChangeEvent<object> | null, newTab: PageTab) {
    navigate(`/proposals/active/${pageAction}/${proposalId?.value}/${newTab}`);
    setUserSelectedReviewField(undefined);
  }

  function handleManageReviewers(event: React.MouseEvent<HTMLButtonElement>) {
    setReviewerSelectorAnchor(event.currentTarget);
    setIsManagingReviewers(true);
  }

  async function handleSubmitRevision() {
    if (!proposal?.redline) throw new Error("No proposal redline to submit.");

    try {
      dispatch(saveComments({ session }));
      let message: string;
      const clientReviewers = proposal?.clientReviewers ?? proposal?.clientReviewers ?? [];
      const vendorReviewers = proposal?.vendorReviewers ?? proposal?.vendorReviewers ?? [];
      if (
        (session.context?.viewingAsVendor &&
          vendorReviewers.length &&
          vendorReviewers.length !== vendorReviewers.filter((reviewer) => reviewer.dateApproved).length) ||
        (!session.context?.viewingAsVendor &&
          clientReviewers.length &&
          clientReviewers.length !== clientReviewers.filter((reviewer) => reviewer.dateApproved).length)
      ) {
        message =
          "Not all reviewers have reviewed the changes. Do you still want to submit them to the other" + " party?";
      } else {
        message = "Doing so will send all your requested changes for review by the other party.";
      }

      const response = await confirm({
        title: "Send Requested Changes?",
        message: message,
        okButtonText: "Send",
      });

      if (response === ConfirmResponse.Cancel) return;

      await requestRevisionAPICommand({ proposal, session }).unwrap();

      enqueueSnackbar("Requested changes sent", { variant: "success" });
      navigate("/proposals/active/");
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`${error}`, { variant: "error" });
    }
  }

  async function handleApproveProposal() {
    dispatch(saveComments({ session }));
    const revision = proposal?.redlinedRevision;
    if (!revision) {
      throw new Error("No redlined revision to approve.");
    }

    if (!proposal?.id) {
      console.error("No proposal to approve");
      return;
    }
    if (!proposal.redline?.isResolved) {
      console.error("Cannot approve proposal where redline is not resolved.");
      return;
    }
    if (!proposal?.isEquivalentTo(revision)) {
      console.error("Cannot approve proposal while there are redlined revision changes.");
    }
    if (!session.user?.id) {
      console.error("No user id to approve proposal");
      return;
    }

    const approveResponse = await confirm({
      title: "Approve Proposal?",
      message: "Doing so will approve the proposal and send it to any other parties for their approval.",
      okButtonText: "Approve",
    });

    if (approveResponse === ConfirmResponse.Cancel) return;

    try {
      await approveProposalAPICommand({ proposalId: proposal.id?.value, session }).unwrap();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to approve proposal", { variant: "error" });
      return;
    }

    if (proposal.userCanHire(session.user)) {
      const hireResponse = await confirm({
        title: "Hire Team?",
        message: "Doing so will approve the proposal and hire the vendor-team.",
        okButtonText: "Hire",
      });

      if (hireResponse === ConfirmResponse.Ok) {
        try {
          await hireProposalAPICommand({ proposalId: proposal.id?.value }).unwrap();
          enqueueSnackbar("Proposal approved and vendor-team hired", { variant: "success" });
        } catch (error) {
          console.error(error);
          enqueueSnackbar("Failed to hire vendor-team", { variant: "error" });
        }
      }
    } else {
      enqueueSnackbar("Proposal approved", { variant: "success" });
    }
  }

  async function handleAccept() {
    if (!proposal?.redline) return;

    const acceptResponse = await confirm({
      title: "Accept All Changes?",
      message: "This will mark all changes as accepted.",
      okButtonText: "Accept",
    });

    if (acceptResponse === ConfirmResponse.Cancel) return;

    let updatedRedline = proposal.redline;
    updatedRedline = updatedRedline.accept();
    handleProposalRedlineChanged(updatedRedline);
  }

  async function handleReject() {
    if (!proposal?.redline) return;
    dispatch(saveComments({ session }));

    const rejectResponse = await confirm({
      title: "Reject All Changes?",
      message: "This will mark all changes as rejected.",
      okButtonText: "Reject",
    });

    if (rejectResponse === ConfirmResponse.Cancel) return;

    let updatedRedline = proposal.redline;
    updatedRedline = updatedRedline.reject();
    handleProposalRedlineChanged(updatedRedline);
  }

  async function handleEndNegotiation() {
    if (!proposal) return;
    dispatch(saveComments({ session }));

    try {
      const endResponse = await confirm({
        title: "End Negotiation?",
        message: "Doing so will end proposal negotiations.",
        okButtonText: "End Negotiation",
      });

      if (endResponse === ConfirmResponse.Cancel) return;

      await rejectProposalAPICommand({ proposalId: proposal.id?.value }).unwrap();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to reject proposal", { variant: "error" });
    }
  }

  async function handleCancel() {
    if (!proposal?.id) throw new Error("No proposal to cancel.");

    dispatch(saveComments({ session }));

    try {
      const rejectResponse = await confirm({
        title: "Cancel Proposal?",
        message: "Doing so will end proposal negotiations.",
      });

      if (rejectResponse === ConfirmResponse.Cancel) return;

      navigate(`/proposals/active/`);
      await cancelProposalAPICommand({ proposalId: proposal.id?.value }).unwrap();
      enqueueSnackbar("Proposal canceled", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to cancel proposal", { variant: "error" });
    }
  }

  async function handleHire() {
    if (!proposal?.id) throw new Error("No proposal to hire.");

    dispatch(saveComments({ session }));

    try {
      const reviewers = session.context?.viewingAsVendor ? proposal?.vendorReviewers : proposal?.clientReviewers;
      let message: string;
      if (reviewers.length && reviewers.length !== reviewers.map((reviewer) => reviewer.dateApproved).length) {
        message = "Not all reviewers have reviewed the proposal. Do you still want to hire the selected vendor-team?";
      } else {
        message = "Hire the selected vendor-team?";
      }

      const hireResponse = await confirm({
        title: "Hire?",
        message: message,
        okButtonText: "Hire",
      });

      if (hireResponse === ConfirmResponse.Cancel) return;

      await hireProposalAPICommand({ proposalId: proposal.id?.value }).unwrap();

      enqueueSnackbar("Proposal hired", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to hire vendor", { variant: "error" });
    }
  }

  async function handleUndoChanges() {
    if (!proposal?.redline) return;

    const undoResponse = await confirm({
      title: "Undo All?",
      message: "Doing so will revert all changes.",
      okButtonText: "Undo All",
    });

    if (undoResponse === ConfirmResponse.Cancel) return;

    let updatedRedline = proposal.redline;
    updatedRedline = updatedRedline.undo();
    handleProposalRedlineChanged(updatedRedline);
  }

  function shouldDisableTabs(): boolean {
    return isLoading || isSaving || isManagingReviewers;
  }

  function handleProposalRedlineChanged(newProposalRedline: ProposalRedline) {
    if (proposal?.isArchived) return;
    if (!proposal?.redline) {
      throw new Error("Current proposal redline is missing");
    }
    const updatedProposal = proposal.updateRedline(newProposalRedline);
    dispatch(updateProposal(updatedProposal));
  }

  function handleFeeScheduleTemplatesUpdated(templateIds: Guid[], newProposalRedline?: ProposalRedline) {
    if (!proposal) {
      console.error("No proposal to update fee schedule templates");
      return;
    }
    let updatedProposal: Proposal;
    if (session.context?.viewingAsVendor) {
      updatedProposal = proposal.updateVendorFeeScheduleTemplateIds(templateIds);
    } else {
      updatedProposal = proposal.updateClientFeeScheduleTemplateIds(templateIds);
    }
    if (newProposalRedline) {
      updatedProposal = updatedProposal.updateRedline(newProposalRedline);
    }
    dispatch(addProposal(updatedProposal));
  }

  const handleSave = useCallback(async () => {
    if (!proposal?.redline) {
      console.error("No proposal to save");
      return;
    }

    try {
      setIsSaving(true);

      const savedProposalJSON = await saveProposalAPICommand({
        updatedProposal: proposal,
        session,
      }).unwrap();

      if (savedProposalJSON) {
        dispatch(saveComments({ session }));
        enqueueSnackbar("Changes saved", { variant: "success" });

        if (proposal.areReviewersModified) {
          enqueueSnackbar("Reviewers have been updated/invited", {
            variant: "success",
          });
        }
      } else {
        console.error("Failed to save proposal", saveProposalResult.error);
        enqueueSnackbar("Failed to save proposal", { variant: "error" });
      }
    } catch (error) {
      console.error("Failed to save proposal", error);
      enqueueSnackbar("Failed to save proposal", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  },
    [proposal, saveProposalAPICommand, session, dispatch, saveProposalResult.error],
  );

  const handleSaveAndShareChanges = useCallback(
    async (args?: { proposal?: Proposal; confirmShare?: boolean }): Promise<string | null | undefined> => {
      const { proposal, confirmShare = true } = args ?? {};
      let proposalToSaveAndShare = proposal ?? proposalRef.current;

      if (!proposalToSaveAndShare) {
        console.error("No proposal to save and share");
        enqueueSnackbar("There was a problem saving and sharing the proposal", {
          variant: "error",
        });
        return;
      }

      const userCanShare = proposalToSaveAndShare.userCanShare(session.user);
      const proposalIsModified = proposalToSaveAndShare.isModified;

      if (confirmShare) {
        const response = await confirm({
          title: "Share",
          message: "Are you sure you want to share any pending comments and/or changes for review?",
          okButtonText: "Share",
        });

        if (response === ConfirmResponse.Cancel) return;
      }

      if (arePendingCommentsRef.current) {
        await dispatch(publishComments({ session })).unwrap();
        enqueueSnackbar("Comments published", { variant: "info" });
        if (!userCanShare) return;
      }

      if (!userCanShare) {
        enqueueSnackbar("You do not have permission to share this proposal", {
          variant: "warning",
        });
        return;
      }


      let savedProposalJSON: ProposalJSON | null = null;
      if (proposalIsModified) {
        savedProposalJSON = await shareProposalAPICommand({
          proposal: proposalToSaveAndShare,
          session,
        }).unwrap();
        if (savedProposalJSON) {
          enqueueSnackbar("Proposal changes shared internally", {
            variant: "success",
          });
        } else {
          console.error("Failed to share proposal changes", shareProposalResult.error);
          enqueueSnackbar("Failed to share proposal changes", {
            variant: "error",
          });
          return;
        }
      }

      if (proposalToSaveAndShare) {
        dispatch(updateThreadSubscribers({ proposal: proposalToSaveAndShare, session }));
      }

      return savedProposalJSON?.id ?? proposalToSaveAndShare.id?.value;
    },
    [confirm, session, dispatch, shareProposalAPICommand, shareProposalResult.error],
  );

  function handleCloseCommentsPanel() {
    setCommentsPanelIsOpen(false);
  }

  function handleOpenCommentsPanel(field?: ProposalField, title?: string) {
    if (field) setCurrentCommentField(field);
    if (title) setCommentsPanelTitle(title);
    setCommentsPanelIsOpen(true);
  }

  async function handleMarkReviewedClicked() {
    if (!proposal?.id) throw new Error("No proposal to mark reviewed");

    try {
      const response = await confirm({
        title: "Mark As Reviewed",
        message: "Are you sure you want to mark this proposal as reviewed?",
        okButtonText: "Mark Reviewed",
      });

      if (response === ConfirmResponse.Cancel) return;

      await giveReviewerApprovalAPICommand({ proposalId: proposal.id.value }).unwrap();
      enqueueSnackbar("Proposal marked as reviewed", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to mark proposal as reviewed", { variant: "error" });
    }
  }

  function hasUserApprovedReview(): boolean {
    const reviewers: ProposalReviewer[] = [...(proposal?.clientReviewers ?? []), ...(proposal?.vendorReviewers ?? [])];
    return reviewers.some((reviewer) => reviewer.userId.isEqualTo(session.user?.id) && reviewer.dateApproved);
  }

  function handleCommentsClicked(field: ProposalField, name?: string) {
    if (proposal?.id) {
      handleOpenCommentsPanel(field, name);
    } else {
      setCurrentCommentField(field);
      setCommentsPanelTitle(name);
    }
  }

  async function handleTeamMemberQuitProposal(memberId: Guid): Promise<void> {
    if (!proposal?.id || !memberId.isEqualTo(session.user?.id)) {
      throw new Error("No proposal or invalid member to quit proposal team.");
    }

    try {
      await quitProposalTeamAPICommand({ proposalId: proposal.id.value }).unwrap();
      enqueueSnackbar("You have quit the proposal vendor-team", {
        variant: "success",
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to quit proposal vendor-team", { variant: "error" });
    }
  }

  function getShareButtonLabel() {
    const isSubmitting =
      requestRevisionResult.isLoading ||
      approveProposalResult.isLoading ||
      rejectProposalResult.isLoading ||
      hireProposalResult.isLoading ||
      cancelProposalResult.isLoading ||
      quitProposalTeamResult.isLoading ||
      giveReviewerApprovalResult.isLoading;

    if (isSubmitting) return "Submitting...";
    if (isSaving) return "Saving...";
    if (!hasReviewers) return "No Reviewers";
    if (arePendingComments && !proposal?.hasUnsharedRedlineChanges) {
      return "Share Comments";
    }
    if (proposal?.hasUnsharedRedlineChanges) {
      return "Share Changes";
    }
    return "Nothing To Share";
  }

  const manageReviewersDisabled =
    !proposal ||
    !commentThreads ||
    isSaving ||
    isManagingReviewers ||
    reviewerSelectorAnchor !== undefined ||
    !proposal?.id ||
    isReviewing;
  const reviewers = (session.context?.viewingAsVendor ? proposal?.vendorReviewers : proposal?.clientReviewers) ?? [];
  const totalApprovals = reviewers.filter((reviewer) => reviewer.dateApproved).length;
  const totalReviewers = reviewers.length ?? 0;
  const pageTitle = useMemo(() => {
    if (pageAction === "view") return "View";
    if (pageAction === "revise") return "Revise"
    if (pageAction === "review") return "Review";
    return "View";
  }, [pageAction]);
  let isDirty = areUnsavedComments;
  const disableSave = !isDirty;
  let saveButtonTooltip = "Save changes (just for yourself)";
  if (!isDirty) {
    saveButtonTooltip = "No changes to save";
  }

  const handleNavigateBack = useCallback(async () => {
    if (isDirty) {
      const response = await confirm({
        title: "Discard changes?",
        message: "Are you sure you want to discard changes?",
        okButtonText: "Discard",
        alternativeOkText: disableSave ? undefined : "Save & Close",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;
      if (response === ConfirmResponse.AltOk) {
        await handleSave();
      }
    }

    if (!proposalId) {
      console.warn("Attempting to navigate back from proposal page without a proposal id");
      navigate("/proposals/active");
      return;
    }

    dispatch(
      resetProposalSessionByProposalId({
        id: proposalId,
      }),
    );
    dispatch(clearUnsavedCommentsByProposalId(proposalId));

    navigate("/proposals/active");
  }, [confirm, disableSave, dispatch, handleSave, isDirty, navigate, proposalId]);

  if (!sessionPermissions)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  if (!sessionPermissions.includesAccessToProposals) return <NotAuthorized />;

  // #region Render
  return (
    !proposalByIdAPIQuery.isError && (
      <PageContainer>
        <Main open={commentsPanelIsOpen} showCommentDrawer={!!proposalId && !isLoading}>
          <HeaderContainer>
            <TitleContainer>
              <Chip
                icon={<ArrowBackIcon />}
                size="medium"
                label="Back"
                onClick={handleNavigateBack}
                color="primary"
                sx={{ mr: 2 }}
              />
              <Typography variant="h4">
                <span style={{ color: "gray", marginRight: "8px" }}>Proposals &gt;</span>
                {pageTitle}
              </Typography>
            </TitleContainer>
            <ActionButtonsContainer>
              <IssueTraverser
                disabled={isSubmitting || isSaving || isManagingReviewers}
                issueFields={uniqBy(issues?.entries.map((issue) => issue.field) ?? [], (field) => field.toJSON())}
                unreadCommentFields={uniqBy(
                  commentThreads?.filter((thread) => thread.numUnreadMessages).map((thread) => thread.field) ?? [],
                  (field) => field.toJSON(),
                )}
                currentField={activeReviewField}
                onTraverseToNewField={handleTraverseToNewField}
              />
              <Tooltip title={saveButtonTooltip}>
                <span>
                  <LoadingButton
                    variant="outlined"
                    size="large"
                    loading={isSaving}
                    color="primary"
                    disabled={isSaving || disableSave}
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                  >
                    Save
                  </LoadingButton>
                </span>
              </Tooltip>
              <CommentButton>
                <ButtonContainer>
                  <HighlightCommentButton
                    active={activeReviewField?.isEqualTo(ProposalField.General)}
                    size="medium"
                    color="primary"
                    onClick={() => {
                      handleOpenCommentsPanel(ProposalField.General);
                    }}
                  >
                    <Badge
                      variant={generalFieldUnreadCount ? "standard" : "dot"}
                      color="secondary"
                      overlap="circular"
                      badgeContent={generalFieldUnreadCount > 0 ? generalFieldUnreadCount : undefined}
                      invisible={!commentThreads?.some((thread) => thread.field.isEqualTo(ProposalField.General))}
                    >
                      <CommentIcon fontSize="medium" />
                    </Badge>
                  </HighlightCommentButton>
                  <CommentButtonLabel variant="button" color="primary">
                    Comments
                  </CommentButtonLabel>
                </ButtonContainer>
              </CommentButton>
            </ActionButtonsContainer>
          </HeaderContainer>
          <Header>
            <TabsContainer
              variant="scrollable"
              scrollButtons="auto"
              indicatorColor="primary"
              textColor="primary"
              value={activeTab}
              onChange={handleTabChange}
            >
              <ProposalTab
                value={PageTab.Details}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.Details}
                  />
                }
              />
              <ProposalTab
                value={PageTab.Team}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.Team}
                  />
                }
              />
              <ProposalTab
                value={PageTab.FeeSchedule}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.FeeSchedule}
                  />
                }
              />
              <ProposalTab
                value={PageTab.Conflicts}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.Conflicts}
                  />
                }
              />
              <ProposalTab
                value={PageTab.Policies}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.Policies}
                  />
                }
              />
              <ProposalTab
                value={PageTab.Discount}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.Discount}
                  />
                }
              />
            </TabsContainer>
          </Header>
          <ActionsContainer>
            <ProposalActions>
              <Fieldset>
                {isReviewing && (
                  <Tooltip
                    enterDelay={600}
                    placement="bottom"
                    title={hasUserApprovedReview() ? "Proposal marked as reviewed" : "Mark proposal as reviewed"}
                  >
                    <span>
                      <ProposalActionButton
                        variant="outlined"
                        color="primary"
                        startIcon={<GradingIcon />}
                        loading={isSaving}
                        disabled={
                          isLoading || isSaving || isSubmitting || isManagingReviewers || hasUserApprovedReview()
                        }
                        onClick={handleMarkReviewedClicked}
                      >
                        {hasUserApprovedReview() ? "Reviewed" : "Mark Reviewed"}
                      </ProposalActionButton>
                    </span>
                  </Tooltip>
                )}
                {!isReviewing && (
                  <>
                    <Tooltip enterDelay={600} placement="bottom" title={"Add/remove proposal reviewers"}>
                      <span>
                        <ProposalActionButton
                          variant="outlined"
                          color="primary"
                          startIcon={<GroupsIcon />}
                          endIcon={
                            <ReviewerBadge
                              overlap="rectangular"
                              invisible={
                                manageReviewersDisabled ||
                                !reviewers.length ||
                                !proposal?.redlinedRevision ||
                                (proposal.redline?.isResolved && proposal.isEquivalentTo(proposal.redlinedRevision))
                              }
                              badgeContent={
                                <Tooltip
                                  placement="bottom"
                                  title={
                                    totalApprovals === totalReviewers
                                      ? "All reviewers approved"
                                      : "Awaiting reviewer responses"
                                  }
                                >
                                  <span>
                                    <sup style={{ verticalAlign: "0.2em" }}>{totalApprovals}</sup><span style={{ verticalAlign: 0 }}>&frasl;</span><sub style={{ verticalAlign: "-0.2em" }}>{totalReviewers}</sub>
                                  </span>
                                </Tooltip>
                              }
                              color={totalReviewers === totalApprovals ? "success" : "warning"}
                            />
                          }
                          loading={false}
                          disabled={
                            isLoading ||
                            isSaving ||
                            isManagingReviewers ||
                            reviewerSelectorAnchor !== undefined ||
                            !proposal?.id
                          }
                          onClick={handleManageReviewers}
                        >
                          Reviewers
                        </ProposalActionButton>
                      </span>
                    </Tooltip>
                    {proposal && (
                      <ReviewerSelector
                        proposal={proposal}
                        popoverAnchor={reviewerSelectorAnchor}
                        onPopoverClose={() => {
                          setReviewerSelectorAnchor(undefined);
                          setIsManagingReviewers(false);
                        }}
                        onReviewersChanged={(updatedReviewers) => {
                          if (!session.context) throw new Error("Session not properly registered.");
                          return session.context?.viewingAsVendor
                            ? dispatch(addProposal(proposal?.updateVendorReviewers(updatedReviewers)))
                            : dispatch(addProposal(proposal?.updateClientReviewers(updatedReviewers)));
                        }}
                        onSend={() => handleSaveAndShareChanges({ proposal, confirmShare: true })}
                      />
                    )}
                  </>
                )}
                <Tooltip
                  enterDelay={600}
                  placement="bottom"
                  title={
                    proposal?.hasUnsavedRedlineChanges || arePendingComments
                      ? "Share redline changes and/or comments for review"
                      : "No reviewers or no unshared changes/comments"
                  }
                >
                  <span>
                    <ProposalActionButton
                      variant="outlined"
                      color="primary"
                      startIcon={<IosShareIcon />}
                      loading={isSaving}
                      disabled={
                        (!arePendingComments && !proposal?.hasUnsavedRedlineChanges) ||
                        isSaving ||
                        isSubmitting ||
                        isManagingReviewers ||
                        !hasReviewers
                      }
                      onClick={() => proposal && handleSaveAndShareChanges({ proposal, confirmShare: true })}
                    >
                      {getShareButtonLabel()}
                    </ProposalActionButton>
                  </span>
                </Tooltip>
              </Fieldset>
              {!disableEditing &&
                proposal?.supersedes &&
                !proposal?.userCanHire(session.user) &&
                !proposal.creator?.userId.isEqualTo(session.user?.id) &&
                !proposal.redline?.hasRejections &&
                !proposal.redline?.hasRevisions &&
                !proposal.redline?.isResolved && (
                  <>
                    <Tooltip enterDelay={600} placement="bottom" title="Accept all changes">
                      <span>
                        <ProposalActionButton
                          variant="outlined"
                          color="success"
                          startIcon={<DoneAllIcon />}
                          loading={false}
                          disabled={isSubmitting || isLoading || isSaving || isManagingReviewers}
                          onClick={handleAccept}
                        >
                          Accept All
                        </ProposalActionButton>
                      </span>
                    </Tooltip>
                    <Tooltip enterDelay={600} placement="bottom" title="Reject all changes">
                      <span>
                        <ProposalActionButton
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon />}
                          loading={false}
                          disabled={
                            isSubmitting ||
                            isLoading ||
                            isSaving ||
                            isManagingReviewers ||
                            reviewerSelectorAnchor !== undefined ||
                            !proposal?.id ||
                            proposal.creator?.userId.isEqualTo(session.user?.id) ||
                            !proposal.userCanReject(session.user) ||
                            proposal.redline?.isResolved
                          }
                          onClick={handleReject}
                        >
                          Reject All
                        </ProposalActionButton>
                      </span>
                    </Tooltip>
                  </>
                )}
              {!disableEditing && !isReviewing && proposal?.creator?.userId.isEqualTo(session.user?.id) && (
                <Tooltip enterDelay={600} placement="bottom" title={"Cancel this proposal and end negotiations"}>
                  <span>
                    <ProposalActionButton
                      variant="outlined"
                      color="error"
                      startIcon={<CloseIcon />}
                      loading={false}
                      disabled={
                        isSubmitting ||
                        isLoading ||
                        isSaving ||
                        isManagingReviewers ||
                        reviewerSelectorAnchor !== undefined ||
                        !proposal?.id
                      }
                      onClick={handleCancel}
                    >
                      Cancel Proposal
                    </ProposalActionButton>
                  </span>
                </Tooltip>
              )}
              {proposal?.userCanApprove(session.user) &&
                proposal?.redline?.isResolved &&
                proposal.redlinedRevision &&
                proposal?.isEquivalentTo(proposal.redlinedRevision) && (
                  <Tooltip
                    enterDelay={600}
                    placement="bottom"
                    title={issues?.entries.length === 0 ? "Submit Approval" : "Resolve Issues to Approve Proposal"}
                  >
                    <span>
                      <ProposalActionButton
                        variant="outlined"
                        color="success"
                        startIcon={<SendIcon />}
                        loading={isSubmitting}
                        disabled={
                          isSubmitting ||
                          isLoading ||
                          isSaving ||
                          isManagingReviewers ||
                          !proposal?.redline?.isResolved ||
                          (!proposal?.userCanApprove(session.user) && isReviewing) ||
                          issues?.entries.length !== 0
                        }
                        onClick={handleApproveProposal}
                      >
                        Submit Approval
                      </ProposalActionButton>
                    </span>
                  </Tooltip>
                )}

              {!disableEditing && proposal?.userCanRevise(session.user) && (
                <Tooltip
                  enterDelay={600}
                  placement="bottom"
                  title={issues?.canSubmit ? "Submit Changes" : "Resolve Issues to Submit"}
                >
                  <span>
                    <ProposalActionButton
                      variant="outlined"
                      color="success"
                      startIcon={<SendIcon />}
                      loading={isSubmitting}
                      disabled={
                        isSubmitting ||
                        isLoading ||
                        isSaving ||
                        isManagingReviewers ||
                        !proposal.redline?.isResolved ||
                        (issues && !issues.canSubmit) ||
                        !proposal.isModifiedByRedline
                      }
                      onClick={handleSubmitRevision}
                    >
                      Submit Changes
                    </ProposalActionButton>
                  </span>
                </Tooltip>
              )}

              {!isReviewing && !disableEditing && proposal?.userIsClient(session.user) && (
                <Tooltip
                  enterDelay={600}
                  placement="bottom"
                  title={!proposal?.userCanHire(session.user) && "Awaiting approval by vendors"}
                >
                  <span>
                    <ProposalActionButton
                      variant="outlined"
                      color="success"
                      startIcon={<WorkIcon />}
                      loading={false}
                      disabled={
                        isSubmitting ||
                        isLoading ||
                        isSaving ||
                        isManagingReviewers ||
                        reviewerSelectorAnchor !== undefined ||
                        !proposal?.userCanHire(session.user)
                      }
                      onClick={handleHire}
                    >
                      Hire
                    </ProposalActionButton>
                  </span>
                </Tooltip>
              )}
              {!disableEditing && proposal?.redline?.canBeUndone && (
                <ProposalActionButton
                  variant="outlined"
                  color="secondary"
                  startIcon={<UndoIcon />}
                  loading={false}
                  disabled={isSubmitting || isLoading || isSaving || isManagingReviewers}
                  onClick={handleUndoChanges}
                >
                  Undo Changes
                </ProposalActionButton>
              )}
              {!disableEditing && !isReviewing && !proposal?.creator?.userId.isEqualTo(session.user?.id) && (
                <ProposalActionButton
                  variant="outlined"
                  color="error"
                  startIcon={<BlockIcon />}
                  loading={false}
                  disabled={
                    isSubmitting ||
                    isLoading ||
                    isSaving ||
                    isManagingReviewers ||
                    reviewerSelectorAnchor !== undefined ||
                    !proposal?.id
                  }
                  onClick={handleEndNegotiation}
                >
                  End Negotiation
                </ProposalActionButton>
              )}
            </ProposalActions>
          </ActionsContainer>
          <Content>
            <TabContent>
              {(isLoading || !proposal?.redline || !proposal.clientTeam) && <Loader />}
              {!isLoading && proposal?.redline && proposal.clientTeam && (
                <>
                  {activeTab === PageTab.Details && (
                    <DetailsTab
                      proposalRedline={proposal.redline}
                      issues={issues}
                      disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                      activeReviewField={activeReviewField}
                      commentThreads={commentThreads ?? []}
                      onProposalRedlineChange={handleProposalRedlineChanged}
                      onTraverseToNewField={handleTraverseToNewField}
                      onCommentsClicked={handleCommentsClicked}
                      clientLeaderUserId={
                        Masked.isPresentNotMasked(proposal.clientLeader) ? proposal.clientLeader.userId : null
                      }
                    />
                  )}
                  {activeTab === PageTab.Team && (
                    <TeamTab
                      proposalRedline={proposal.redline}
                      issues={issues}
                      disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                      activeReviewField={activeReviewField}
                      commentThreads={commentThreads ?? []}
                      teamLeader={
                        MaskedTeam.isPresentNotMasked(proposal.vendorTeam) ? proposal.vendorTeam.leader : undefined
                      }
                      onProposalRedlineChange={handleProposalRedlineChanged}
                      onTraverseToNewField={handleTraverseToNewField}
                      onCommentsClicked={handleCommentsClicked}
                      onTeamMemberQuitProposal={handleTeamMemberQuitProposal}
                    />
                  )}
                  {activeTab === PageTab.FeeSchedule && (
                    <FeeScheduleTab
                      proposalRedline={proposal.redline}
                      issues={issues}
                      disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                      activeReviewField={activeReviewField}
                      commentThreads={commentThreads ?? []}
                      onProposalRedlineChange={handleProposalRedlineChanged}
                      onCommentsClicked={handleCommentsClicked}
                      onFeeScheduleTemplatesUpdated={handleFeeScheduleTemplatesUpdated}
                    />
                  )}
                  {activeTab === PageTab.Conflicts && (
                    <ConflictsTab
                      proposalRedline={proposal.redline}
                      issues={issues}
                      disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                      activeReviewField={activeReviewField}
                      commentsToolTip={"Comments"}
                      commentThreads={commentThreads ?? []}
                      onProposalRedlineChange={handleProposalRedlineChanged}
                      onTraverseToNewField={handleTraverseToNewField}
                      onCommentsClicked={handleCommentsClicked}
                    />
                  )}
                  {activeTab === PageTab.Policies && (
                    <PoliciesTab
                      proposalRedline={proposal.redline}
                      issues={issues}
                      disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                      activeReviewField={activeReviewField}
                      commentsToolTip={"Comments"}
                      commentThreads={commentThreads ?? []}
                      onProposalRedlineChange={handleProposalRedlineChanged}
                      onTraverseToNewField={handleTraverseToNewField}
                      onCommentsClicked={handleCommentsClicked}
                    />
                  )}
                  {activeTab === PageTab.Discount && (
                    <DiscountTab
                      proposalRedline={proposal.redline}
                      issues={issues}
                      disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                      activeReviewField={activeReviewField}
                      commentsToolTip={"Comments"}
                      commentThreads={commentThreads ?? []}
                      onProposalRedlineChange={handleProposalRedlineChanged}
                      onTraverseToNewField={handleTraverseToNewField}
                      onCommentsClicked={handleCommentsClicked}
                    />
                  )}
                </>
              )}
            </TabContent>
          </Content>
        </Main>
        {proposal && commentThreads && (
          <CommentsDrawer
            proposal={proposal}
            commentThreads={commentThreads}
            currentCommentField={currentCommentField}
            activeReviewField={activeReviewField}
            isOpen={commentsPanelIsOpen}
            isSaving={isSaving}
            title={commentsPanelTitle}
            onOpen={handleOpenCommentsPanel}
            onClose={handleCloseCommentsPanel}
          />
        )}
      </PageContainer>
    )
  );
}

function proposalFieldCategoryFromPageTab(pageTab: PageTab): ProposalFieldCategoryType {
  switch (pageTab) {
    case PageTab.Details: return ProposalFieldCategory.Details;
    case PageTab.Team: return ProposalFieldCategory.Team;
    case PageTab.FeeSchedule: return ProposalFieldCategory.FeeSchedule;
    case PageTab.Conflicts: return ProposalFieldCategory.Conflicts;
    case PageTab.Policies: return ProposalFieldCategory.Policies;
    case PageTab.Discount: return ProposalFieldCategory.Discount;
  }
}

function pageTabFromProposalFieldCategory(proposalFieldCategory: ProposalFieldCategoryType): PageTab {
  switch (proposalFieldCategory) {
    case ProposalFieldCategory.Details: return PageTab.Details;
    case ProposalFieldCategory.Team: return PageTab.Team;
    case ProposalFieldCategory.FeeSchedule: return PageTab.FeeSchedule;
    case ProposalFieldCategory.Conflicts: return PageTab.Conflicts;
    case ProposalFieldCategory.Policies: return PageTab.Policies;
    case ProposalFieldCategory.Discount: return PageTab.Discount;
  }
}
